/**
 * lib/accessPassApi.js — Diplomexa AccessPass HTTP client
 * -------------------------------------------------------
 * Thin wrappers over the `/api/events/access/*` and
 * `/api/admin/events/{event_id}/access/*` surfaces.  Mirrors
 * `gamificationApi.js` / `trainerGradingApi.js` in style — one
 * named export per backend route, zero state.
 */
import api from "./api";

// ──────────────────────────────────────────────────────────────────────
// Public
// ──────────────────────────────────────────────────────────────────────
export async function getPublicAccessInfo(slug) {
  const r = await api.get(`/events/${slug}/access-info`);
  return r.data;
}

export async function visitorRegister(eventId, payload) {
  const r = await api.post(`/events/${eventId}/visitor-register`, payload);
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// User
// ──────────────────────────────────────────────────────────────────────
export async function getMyPasses() {
  const r = await api.get(`/events/access/my-passes`);
  return r.data;
}

export async function getPassWithQr(passId) {
  const r = await api.get(`/events/access/pass/${passId}`);
  return r.data;
}

export async function resendPass(passId) {
  const r = await api.post(`/events/access/pass/${passId}/resend`);
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// Scanner
// ──────────────────────────────────────────────────────────────────────
export async function scanToken(payload) {
  const r = await api.post(`/events/access/scan`, payload);
  return r.data;
}

export async function manualVerify(payload) {
  const r = await api.post(`/events/access/manual-verify`, payload);
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// Admin
// ──────────────────────────────────────────────────────────────────────
export async function adminListPasses(eventId, params = {}) {
  const r = await api.get(
    `/admin/events/${eventId}/access/passes`, { params }
  );
  return r.data;
}

export async function adminCreatePass(eventId, payload) {
  const r = await api.post(
    `/admin/events/${eventId}/access/pass/create`, payload
  );
  return r.data;
}

export async function adminPatchPass(passId, payload) {
  const r = await api.patch(
    `/admin/events/access/pass/${passId}`, payload
  );
  return r.data;
}

export async function adminApprovePass(passId) {
  const r = await api.post(
    `/admin/events/access/pass/${passId}/approve`
  );
  return r.data;
}

export async function adminRejectPass(passId, reason = "") {
  const r = await api.post(
    `/admin/events/access/pass/${passId}/reject`, { reason }
  );
  return r.data;
}

export async function adminRevokePass(passId, reason = "") {
  const r = await api.post(
    `/admin/events/access/pass/${passId}/revoke`, { reason }
  );
  return r.data;
}

export async function adminListVisitorRegs(eventId, params = {}) {
  const r = await api.get(
    `/admin/events/${eventId}/access/visitor-registrations`,
    { params }
  );
  return r.data;
}

export async function adminListLogs(eventId, params = {}) {
  const r = await api.get(
    `/admin/events/${eventId}/access/logs`, { params }
  );
  return r.data;
}

export async function adminGetAnalytics(eventId) {
  const r = await api.get(
    `/admin/events/${eventId}/access/analytics`
  );
  return r.data;
}

export async function adminListZones(eventId) {
  const r = await api.get(
    `/admin/events/${eventId}/access/zones`
  );
  return r.data;
}

export async function adminCreateZone(eventId, payload) {
  const r = await api.post(
    `/admin/events/${eventId}/access/zones`, payload
  );
  return r.data;
}

export async function adminPatchZone(zoneId, payload) {
  const r = await api.patch(
    `/admin/events/access/zones/${zoneId}`, payload
  );
  return r.data;
}

export async function adminGetSettings(eventId) {
  const r = await api.get(
    `/admin/events/${eventId}/access/settings`
  );
  return r.data;
}

export async function adminPatchSettings(eventId, payload) {
  const r = await api.patch(
    `/admin/events/${eventId}/access/settings`, payload
  );
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// Photo upload
// ──────────────────────────────────────────────────────────────────────
export async function visitorPhotoPre(file) {
  const fd = new FormData();
  fd.append("photo", file);
  const r = await api.post(`/events/access/visitor-photo-pre`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

export async function uploadPassPhoto(passId, file) {
  const fd = new FormData();
  fd.append("photo", file);
  const r = await api.post(`/events/access/pass/${passId}/photo`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// Diploks-priced premium actions  (Chunk A)
// ──────────────────────────────────────────────────────────────────────
export async function lostPassReissue(passId, reason = "lost_pass") {
  const r = await api.post(
    `/events/access/pass/${passId}/lost-reissue`,
    { reason }
  );
  return r.data;
}

export async function downloadParentReport(childUserId) {
  // Returns a blob — UI converts to download.
  const r = await api.post(
    `/events/access/parent-report/${childUserId}`,
    {},
    { responseType: "blob" }
  );
  return {
    blob: r.data,
    diploksCharged: r.headers["x-diploks-charged"],
    balanceAfter: r.headers["x-diploks-balance-after"],
  };
}

export async function getAccessFeatureCosts() {
  const r = await api.get(`/events/access/feature-costs`);
  return r.data;
}

// ──────────────────────────────────────────────────────────────────────
// Chunk B — Paid visitor passes (Razorpay / Stripe)
// ──────────────────────────────────────────────────────────────────────
export async function adminGetPricing(eventId) {
  const r = await api.get(`/admin/events/${eventId}/access/pricing`);
  return r.data;
}

export async function adminPatchPricing(eventId, payload) {
  const r = await api.patch(`/admin/events/${eventId}/access/pricing`, payload);
  return r.data;
}

export async function startCheckout(passId, opts = {}) {
  const r = await api.post(`/events/access/checkout/start`, {
    pass_id: passId,
    billing_country: opts.billingCountry || null,
    currency_hint: opts.currencyHint || null,
  });
  return r.data;
}

export async function verifyRazorpay(payload) {
  const r = await api.post(`/events/access/checkout/verify-razorpay`, payload);
  return r.data;
}

export async function adminRevokeWithRefund(passId, payload) {
  const r = await api.post(
    `/admin/events/access/pass/${passId}/revoke-with-refund`,
    payload
  );
  return r.data;
}

export async function getPaymentStatus(passId) {
  const r = await api.get(`/events/access/pass/${passId}/payment-status`);
  return r.data;
}

export { formatApiError } from "./api";
